import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Settings, Shield, Zap, MessageCircle, Leaf, Recycle, Globe2, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal } from '../hooks/useReveal';

const ABOUT_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';
const WELDING_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gfe4xo80_WhatsApp%20Image%202024-11-17%20at%2021.04.49%20%281%29.jpeg';
const MAP_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/zcgpbexp_1746633955032.jpg';

export default function About() {
  const { t } = useLanguage();

  const values = [
    { key: 'production', icon: <Settings className="w-6 h-6" />, span: 'md:col-span-7' },
    { key: 'service', icon: <Shield className="w-6 h-6" />, span: 'md:col-span-5' },
    { key: 'management', icon: <Zap className="w-6 h-6" />, span: 'md:col-span-5' },
    { key: 'communication', icon: <MessageCircle className="w-6 h-6" />, span: 'md:col-span-7' },
  ];

  const sustainItems = [
    { icon: <Leaf className="w-5 h-5" />, title: 'Reducing Carbon Footprint', desc: 'We prioritize the reduction of our carbon footprint by optimizing production processes and investing in energy-efficient technologies.' },
    { icon: <Recycle className="w-5 h-5" />, title: 'Recycling & Waste Management', desc: 'We implement comprehensive recycling programs that ensure materials are recycled and reused whenever feasible.' },
    { icon: <Globe2 className="w-5 h-5" />, title: 'Eco-Friendly Design', desc: 'Our transformers are designed with sustainability as a guiding principle, using environmentally friendly materials.' },
    { icon: <GraduationCap className="w-5 h-5" />, title: 'Community & Education', desc: 'Through educational initiatives and partnerships, we raise awareness about environmental stewardship.' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={ABOUT_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-[#05060A]/60 to-[#05060A]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h1 data-testid="about-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white">
            {t('about_section.label')}
          </h1>
        </div>
      </section>

      {/* About Intro */}
      <section className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6 leading-tight">
                {t('about_section.title')}
              </h2>
              <p className="text-base text-slate-300 leading-relaxed">
                {t('about_section.description')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values Bento Grid */}
      <section data-testid="values-grid" className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-16">
            <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">
              {t('about_section.subtitle')}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {values.map((v) => (
              <Reveal key={v.key} className={v.span}>
                <div className="h-full border border-white/10 bg-[#05060A] p-8 md:p-12 hover:bg-[#111620] transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 mb-6 group-hover:bg-blue-600/20 transition-colors">
                    {v.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">{t(`values.${v.key}.title`)}</h3>
                  <p className="text-xs text-blue-400 uppercase tracking-widest mb-4">{t(`values.${v.key}.subtitle`)}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(`values.${v.key}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quality */}
      <section className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <Reveal>
              <img src={WELDING_IMG} alt="Quality" className="w-full h-[400px] object-cover rounded-sm" />
            </Reveal>
            <Reveal>
              <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">{t('quality_section.label')}</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">
                {t('quality_section.title')}
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-6">
                {t('quality_section.subtitle')}
              </p>
              <Link to="/quality">
                <Button data-testid="about-quality-link" className="bg-blue-600 text-white rounded-sm px-8 py-4 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                  {t('common.learn_more')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-20 md:py-32 bg-[#0B0E14] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={MAP_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/60 to-[#0B0E14]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4 max-w-2xl mx-auto">
              We export our transformers to countries across multiple continents.
            </h2>
            <p className="text-slate-400 text-base">Drawing our strength from the world.</p>
          </Reveal>
        </div>
      </section>

      {/* Sustainability */}
      <section data-testid="sustainability-section" className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-16">
            <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">Commitment</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t('sustainability.title')}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sustainItems.map((item, i) => (
              <Reveal key={i}>
                <div className="border border-white/10 bg-[#0B0E14] p-8 hover:border-blue-600/30 transition-all duration-300 flex gap-6">
                  <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-[#0B0E14] border-y border-white/5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-20 px-10">
              {['ISO 9001', 'ISO 14001', 'ISO 45001', 'IEC 60076', 'KEMA', 'TSE', 'CE'].map((cert) => (
                <span key={`${idx}-${cert}`} className="text-2xl font-bold text-white/10 tracking-widest uppercase">{cert}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">{t('contact_section.subtitle')}</h2>
            <Link to="/contact">
              <Button data-testid="about-contact-cta" className="bg-blue-600 text-white rounded-sm px-10 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                {t('common.get_in_touch')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
